import { z } from 'zod';

import { variantRegistry } from './variantRegistry';

const validVariants = Object.keys(variantRegistry) as Array<
  keyof typeof variantRegistry
>;

export const variantEnvSchema = z.object({
  VITE_VARIANT: z
    .enum(validVariants as [keyof typeof variantRegistry])
    .optional()
    .default('synaps')
    .refine(
      (value) =>
        value === undefined ||
        validVariants.includes(value as keyof typeof variantRegistry),
      {
        message: `VITE_VARIANT must be one of: ${validVariants.join(', ')} or null/undefined`,
      }
    ),
});

export type ValidatedEnv = z.infer<typeof variantEnvSchema>;

export const validateVariantEnv = () => {
  const env = {
    VITE_VARIANT: import.meta.env.VITE_VARIANT,
  };

  try {
    const validatedEnv = variantEnvSchema.parse(env);
    return {
      success: true as const,
      data: validatedEnv,
      error: null,
    };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errorMessage = error.errors
        .map((err) => `${err.path.join('.')}: ${err.message}`)
        .join('; ');

      console.error('Environment validation failed:', errorMessage);

      return {
        success: false as const,
        data: null,
        error: errorMessage,
      };
    }

    const errorMessage = 'Unknown validation error';
    console.error('Environment validation failed:', error);

    return {
      success: false as const,
      data: null,
      error: errorMessage,
    };
  }
};

export const getValidatedVariant = (): keyof typeof variantRegistry => {
  const validation = validateVariantEnv();

  if (!validation.success) {
    console.warn(
      `Variant validation failed: ${validation.error}. Falling back to 'synaps'`
    );
    return 'synaps';
  }

  return (validation.data.VITE_VARIANT ||
    'synaps') as keyof typeof variantRegistry;
};
